import type { EvaluationDraftData, EvaluationState } from "@/hooks/queries/evaluation-types";
// Type-only import — erased at compile, so the ResultsStep React.lazy boundary
// stays intact (a runtime import here would pull the results bundle into the shell).
import type { ResultsView } from "@/routes/evaluation/results/ResultsStep";

// `current_step` source + restoration (Story 6.7, Task 6 / AC #10).
//
// The epic AC says "open the step indicated by `evaluation.state`", but `state` is
// the locked lifecycle enum (draft|submitted|scored|archived), NOT a step — a draft
// spans setup→review. There is no `current_step` column. The resolution: the FE
// writes `draft_data.current_step` on each save (6.3/6.4/6.5), and resume reads it
// back. For drafts saved before `current_step` existed (and the empty `{}` default),
// resume DERIVES the furthest-completed step. `resolveResumeStep` does both — read
// `current_step` if valid, else derive — so the step stories can start writing
// `current_step` later without reworking 6.7.

/** The five rail steps (Screens 04–08). Order is the rail order. */
export const STEP_IDS = ["setup", "tasks", "scoring", "review", "results"] as const;

export type StepId = (typeof STEP_IDS)[number];

export interface StepMeta {
  id: StepId;
  label: string;
  /** Sidebar-note blurb, mirroring the per-step line in Screens 04–08. */
  description: string;
}

/** Screen-36 processing blurb — the fail-safe floor for the results rail entry. */
const RESULTS_PROCESSING_DESCRIPTION =
  "Scoring in progress. Results will appear here when complete.";

/**
 * The three scoring-failure titles, single-sourced on the NON-LAZY side of the
 * React.lazy results boundary (gh-567 review F2). The lazy consumers —
 * ResultsStep's aria-live LIVE_MESSAGE and ScoringFailedCard's banner titles —
 * import from here: a lazy→shell runtime import is bundle-safe, while the
 * reverse direction would pull the results chunk into the shell (which is why
 * these strings cannot live in that module).
 */
export const SCORING_FAILURE_COPY: Record<"failed" | "blocked" | "timeout", string> = {
  failed: "Scoring could not complete.",
  blocked: "Scoring is blocked.",
  timeout: "Scoring is taking longer than expected.",
};

/** The rail definition — single source for the shell rail + the "Step N of 5" note.
 *
 * gh-567 — the `results` entry is the FAIL-SAFE floor (screen-36 processing copy),
 * not the completed claim: the results step's real blurb is state-aware
 * (`resultsRailDescription` below, driven by ResultsStep's reported view), and a
 * render site that misses the override must degrade to "in progress" — never to a
 * false "results ready" beside a processing panel (the gh-567 contradiction). */
export const STEP_RAIL: readonly StepMeta[] = [
  { id: "setup", label: "Evaluation setup", description: "Evaluation context and diagnosis." },
  { id: "tasks", label: "Tasks", description: "Observed task selection (2–4 tasks)." },
  { id: "scoring", label: "Scoring", description: "Ordinal scoring across the selected tasks." },
  { id: "review", label: "Review", description: "Review entries before submitting." },
  { id: "results", label: "Results", description: RESULTS_PROCESSING_DESCRIPTION },
];

/**
 * State-aware rail blurb for the results step (gh-567). Copy sources:
 * `processing`/null → screen-36 verbatim; `completed` → screen-08 verbatim
 * (incl. "for review"); the failure states share `SCORING_FAILURE_COPY` above
 * with ResultsStep's aria-live map and ScoringFailedCard's titles.
 */
export function resultsRailDescription(view: ResultsView | null): string {
  switch (view) {
    case "completed":
      // Screen-08 sidebar line verbatim — DELIBERATELY not LIVE_MESSAGE.completed
      // ("Scoring complete. Your results are ready.", the aria-live phrasing).
      // Pinned by the literal in resolve-resume-step.test.ts; a screen-08 copy
      // edit must update both together. (review F3)
      return "Scoring completed and results ready for review.";
    case "failed":
    case "blocked":
    case "timeout":
      return SCORING_FAILURE_COPY[view];
    case "processing":
    case null:
      return RESULTS_PROCESSING_DESCRIPTION;
    default: {
      // Exhaustive at compile time — a future ResultsView variant fails the build
      // here, forcing a copy decision — while the RUNTIME floor stays the
      // fail-safe processing blurb, never a throw on a clinical screen. (review F4)
      const unreachable: never = view;
      void unreachable;
      return RESULTS_PROCESSING_DESCRIPTION;
    }
  }
}

/** 0-based index of a step on the rail (for "Step N of 5" + active styling). */
export function stepIndex(step: StepId): number {
  return STEP_IDS.indexOf(step);
}

function isStepId(value: unknown): value is StepId {
  return typeof value === "string" && (STEP_IDS as readonly string[]).includes(value);
}

function hasAnyScore(scores: EvaluationDraftData["scores"]): boolean {
  if (!scores) return false;
  return Object.values(scores).some(
    (arr) => Array.isArray(arr) && arr.some((v) => v !== null && v !== undefined),
  );
}

function hasAnyTask(tasks: EvaluationDraftData["tasks"]): boolean {
  return Array.isArray(tasks) && tasks.some((t) => Boolean(t?.task_code));
}

/**
 * Pick the step to open a resumed evaluation at.
 *
 *  0. If the evaluation is past submit (`state === "submitted" | "scored"`), open
 *     `results` — the arrival experience (Story 9.7). This wins OVER `current_step`,
 *     which is frozen at its pre-submit value by design (the submit doesn't advance it,
 *     and gh-470c's self-reconcile deliberately excludes `current_step`), so it can never
 *     route a submitted evaluation back into the editor.
 *  1. If `draft_data.current_step` is a known step, honour it exactly (the clinician
 *     resumes where they left off — Screen 02's "Continue → current step").
 *  2. Otherwise DERIVE the furthest-completed step: scores → scoring; ≥1 task →
 *     tasks; otherwise `setup`. (Never derives to review/results — those require an
 *     explicit `current_step` or the submitted-state rule above.)
 *
 * `setup` is the floor: a draft with only diagnosis/context/date started — and a
 * brand-new empty `{}` — both open at setup (the first rail step), so there is no
 * separate diagnosis branch to test. Never a 500 / blank step.
 */
export function resolveResumeStep(
  draft: EvaluationDraftData | null | undefined,
  state?: EvaluationState,
): StepId {
  // A submitted/scored evaluation is frozen — land on results regardless of current_step.
  if (state === "submitted" || state === "scored") return "results";
  if (!draft) return "setup";
  if (isStepId(draft.current_step)) return draft.current_step;
  if (hasAnyScore(draft.scores)) return "scoring";
  if (hasAnyTask(draft.tasks)) return "tasks";
  return "setup";
}
